<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { moneyShort, sizeRange, sizeUnitLabel } from './helpers';
import { STATUS_STYLE } from './constants';
import { apiErrorMessage } from '../../../api/realEstate/errors';
import ActiveSwitch from './components/ActiveSwitch.vue';

export default {
  name: 'ProjectsTable',
  components: { ActiveSwitch },
  props: {
    projects: {
      type: Array,
      required: true,
    },
  },
  data() {
    return { togglingId: null };
  },
  methods: {
    ...mapActions('realEstate', ['setActive']),
    statusClass(project) {
      return STATUS_STYLE[project.project_status] || '';
    },
    priceLabel(project) {
      const { rollup } = project;
      if (rollup.mixed_currency)
        return this.$t('REAL_ESTATE.CARD.MIXED_CURRENCY');
      if (rollup.price_min == null)
        return this.$t('REAL_ESTATE.CARD.NO_FLOOR_PLANS');
      if (rollup.price_min === rollup.price_max) {
        return moneyShort(rollup.price_min, rollup.currency);
      }
      return `${moneyShort(rollup.price_min, rollup.currency)} – ${moneyShort(
        rollup.price_max,
        rollup.currency
      )}`;
    },
    sizeLabel(project) {
      const range = sizeRange(project.rollup);
      if (range == null) return '—';
      return this.$t('REAL_ESTATE.CARD.SIZE_RANGE', {
        range,
        unit: sizeUnitLabel(this, project.rollup.size_unit),
      });
    },
    openProject(project) {
      this.$router.push({
        name: 'real_estate_project',
        params: {
          accountId: this.$route.params.accountId,
          projectId: project.property_id,
        },
      });
    },
    async handleToggleActive(project) {
      this.togglingId = project.property_id;
      const isActive = !project.is_active;
      try {
        await this.setActive({
          propertyId: project.property_id,
          isActive,
        });
        useAlert(
          this.$t(
            isActive
              ? 'REAL_ESTATE.CARD.SUCCESS_ACTIVE'
              : 'REAL_ESTATE.CARD.SUCCESS_INACTIVE',
            { name: project.name }
          )
        );
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.togglingId = null;
      }
    },
  },
};
</script>

<template>
  <div
    class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto"
  >
    <table class="w-full text-sm border-collapse">
      <thead>
        <tr
          class="text-left text-slate-400 uppercase text-xs bg-slate-50 dark:bg-slate-700/50"
        >
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_NAME') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_TYPE') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_REGION') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_TENURE') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_PRICE') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_SIZE') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_STATUS') }}
          </th>
          <th class="py-2 px-4 font-medium">
            {{ $t('REAL_ESTATE.LIST.TABLE_COLUMN_ACTIVE') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="project in projects"
          :key="project.property_id"
          class="border-b border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
          :class="{ 'opacity-60': !project.is_active }"
          @click="openProject(project)"
        >
          <td class="py-2 px-4 text-slate-900 dark:text-slate-100 font-medium">
            {{ project.name }}
          </td>
          <td class="py-2 px-4 text-slate-600 dark:text-slate-300">
            {{ project.property_type
            }}{{
              project.property_subtype ? ` · ${project.property_subtype}` : ''
            }}
          </td>
          <td class="py-2 px-4 text-slate-600 dark:text-slate-300">
            {{ project.region || project.area || '—' }}
          </td>
          <td class="py-2 px-4 text-slate-600 dark:text-slate-300">
            {{ project.tenure || '—' }}
          </td>
          <td class="py-2 px-4 text-slate-600 dark:text-slate-300">
            {{ priceLabel(project) }}
          </td>
          <td class="py-2 px-4 text-slate-600 dark:text-slate-300">
            {{ sizeLabel(project) }}
          </td>
          <td class="py-2 px-4">
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              :class="statusClass(project)"
            >
              {{ project.project_status_label || project.project_status }}
            </span>
          </td>
          <td class="py-2 px-4">
            <ActiveSwitch
              :value="project.is_active"
              :disabled="togglingId === project.property_id"
              @toggle="handleToggleActive(project)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
